import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';

/**
 * WS2.3 — consolidated post-import results: one screen telling the operator exactly
 * what landed and what needs attention. Per-resourceType loaded/mapped/invalid/skipped
 * (from the FHIR cache summary) + a quarantine taxonomy grouped by stage. Auto-shown
 * once an import has run so the operator lands on results, not a bare status line.
 */
export function ImportResults({ sessionId, live = false }: { sessionId: string; live?: boolean }) {
  const { data: summary } = useQuery({
    queryKey: ['fhir-cache-summary', sessionId],
    queryFn: () => api.getFhirCacheSummary(sessionId),
    // While an import is running, poll faster so the per-type Loaded counts climb live.
    refetchInterval: live ? 1_500 : 5_000,
  });
  const { data: quar } = useQuery({
    queryKey: ['quarantine', sessionId],
    queryFn: () => api.listQuarantine(sessionId),
  });

  const byType = (summary?.byType ?? []).filter((t) => t.mapped + t.loaded + t.invalid + t.failed + t.skipped > 0);
  const totals = summary?.totals;
  const qRows = quar?.rows ?? [];
  const byStage = qRows.reduce<Record<string, number>>((acc, r) => {
    acc[r.stage] = (acc[r.stage] ?? 0) + 1;
    return acc;
  }, {});

  if (byType.length === 0) return null;

  const num = (n: number, tone?: 'good' | 'bad') => (
    <span style={{ color: n === 0 ? 'var(--text-dimmed)' : tone === 'good' ? '#34d399' : tone === 'bad' ? '#f87171' : 'var(--text-primary)' }}>
      {n}
    </span>
  );
  const cell = 'px-3 py-1.5 text-right tabular-nums';
  const head = 'px-3 py-1.5 text-right text-[10px] font-semibold uppercase tracking-widest';

  return (
    <div className="mt-3">
      <div className="mb-1 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>
        {live ? 'Import progress' : 'Import results'}
        {live && <span className="animate-pulse" style={{ color: 'var(--accent-blue)' }}>● live</span>}
      </div>
      <div className="overflow-hidden rounded-xl" style={{ border: '1px solid var(--border)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'var(--bg-primary)', color: 'var(--text-secondary)' }}>
              <th className={`${head} text-left`}>Resource</th>
              <th className={head}>Loaded</th>
              <th className={head}>Mapped</th>
              <th className={head}>Invalid</th>
              <th className={head}>Failed</th>
              <th className={head}>Skipped</th>
            </tr>
          </thead>
          <tbody>
            {byType.map((t) => (
              <tr key={t.resourceType} style={{ borderTop: '1px solid var(--border)' }}>
                <td className="px-3 py-1.5 font-medium" style={{ color: 'var(--text-primary)' }}>{t.resourceType}</td>
                <td className={cell}>{num(t.loaded, 'good')}</td>
                <td className={cell}>{num(t.mapped)}</td>
                <td className={cell}>{num(t.invalid, 'bad')}</td>
                <td className={cell}>{num(t.failed, 'bad')}</td>
                <td className={cell}>{num(t.skipped)}</td>
              </tr>
            ))}
            {totals && (
              <tr style={{ borderTop: '1px solid var(--border)', background: 'var(--bg-primary)', fontWeight: 600 }}>
                <td className="px-3 py-1.5 text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Total</td>
                <td className={cell}>{num(totals.loaded, 'good')}</td>
                <td className={cell}>{num(totals.mapped)}</td>
                <td className={cell}>{num(totals.invalid, 'bad')}</td>
                <td className={cell}>{num(totals.failed, 'bad')}</td>
                <td className={cell}>{num(totals.skipped)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {qRows.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]" style={{ color: 'var(--text-secondary)' }}>
          <span className="font-semibold" style={{ color: '#f59e0b' }}>{qRows.length} quarantined</span>
          {Object.entries(byStage).map(([stage, n]) => (
            <span key={stage} className="rounded-full px-2 py-0.5" style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
              {stage}: {n}
            </span>
          ))}
          <span style={{ color: 'var(--text-dimmed)' }}>— see the Quarantine tab for details</span>
        </div>
      )}
    </div>
  );
}
